import React from 'react';
import { Badge } from '../ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import { Skeleton } from '../ui/skeleton';

// ─── Event-type colour map ────────────────────────────────────────────────────
// success (green) | failure (red) | change (amber) | neutral (gray)

const EVENT_VARIANT = {
    login_success: 'success',
    login_fail: 'failure',
    login_locked: 'failure',
    password_change: 'change',
    role_change: 'change',
    account_deleted: 'failure',
    'lead.submitted': 'neutral',
    impersonation_start: 'change',
    company_deleted: 'failure',
};

const VARIANT_CLASS = {
    success: 'bg-green-100 text-green-800 border-green-200',
    failure: 'bg-red-100 text-red-800 border-red-200',
    change: 'bg-amber-100 text-amber-800 border-amber-200',
    neutral: 'bg-gray-100 text-gray-700 border-gray-200',
};

function EventBadge({ eventType, t }) {
    const variant = EVENT_VARIANT[eventType] ?? 'neutral';
    const cls = VARIANT_CLASS[variant];
    const label = t(`audit.event.${eventType}`, { defaultValue: eventType });
    return (
        <Badge
            variant="outline"
            className={`text-xs font-medium whitespace-nowrap ${cls}`}
        >
            {label}
        </Badge>
    );
}

// ─── Date formatter ───────────────────────────────────────────────────────────

function formatDateTime(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return new Intl.DateTimeFormat('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        })
            .format(d)
            .replace(',', '');
    } catch {
        return iso;
    }
}

// ─── User cell ────────────────────────────────────────────────────────────────

function resolveUser(event) {
    // impersonation events carry a readable target name
    if (event.details?.target_name) return event.details.target_name;
    if (event.email) return event.email;
    if (event.user_id) return event.user_id;
    return '—';
}

// ─── Details cell ─────────────────────────────────────────────────────────────

function DetailsCell({ details, t }) {
    const keys = Object.keys(details ?? {});
    if (keys.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
        <details className="max-w-xs">
            <summary className="cursor-pointer text-xs text-primary underline underline-offset-2 select-none">
                {t('audit.details_show')}
            </summary>
            <pre className="text-xs bg-muted/50 rounded p-2 mt-1 overflow-auto max-h-40 whitespace-pre-wrap break-words">
                {JSON.stringify(details, null, 2)}
            </pre>
        </details>
    );
}

// ─── Skeleton rows ────────────────────────────────────────────────────────────

function SkeletonRows({ count = 5, cols }) {
    return Array.from({ length: count }).map((_, i) => (
        <TableRow key={i}>
            {Array.from({ length: cols }).map((__, j) => (
                <TableCell key={j}>
                    <Skeleton className="h-4 w-full" />
                </TableCell>
            ))}
        </TableRow>
    ));
}

// ─── Mobile card (single event) ───────────────────────────────────────────────

function EventCard({ event, includeCompanyColumn, t }) {
    const isPreTenant = event.company_id === 'none';
    return (
        <div className="rounded-md border p-3 space-y-2 text-sm">
            <div className="flex items-start justify-between gap-2">
                <EventBadge eventType={event.event_type} t={t} />
                <span className="text-xs text-muted-foreground shrink-0">
                    {formatDateTime(event.created_at)}
                </span>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <span className="text-muted-foreground">{t('audit.column.user')}</span>
                <span className="font-mono break-all">{resolveUser(event)}</span>
                {includeCompanyColumn && (
                    <>
                        <span className="text-muted-foreground">{t('audit.column.company')}</span>
                        <span>
                            {isPreTenant ? (
                                <span
                                    className="text-muted-foreground"
                                    title={t('audit.filter.pre_tenant')}
                                >
                                    —
                                </span>
                            ) : (
                                event.company_id ?? '—'
                            )}
                        </span>
                    </>
                )}
                <span className="text-muted-foreground">{t('audit.column.ip')}</span>
                <span className="font-mono">{event.ip_address ?? '—'}</span>
                {Object.keys(event.details ?? {}).length > 0 && (
                    <>
                        <span className="text-muted-foreground">{t('audit.column.details')}</span>
                        <DetailsCell details={event.details} t={t} />
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {Array}    props.events               - Audit events array from the API
 * @param {boolean}  props.includeCompanyColumn - Show the Company column (super-admin view)
 * @param {boolean}  props.loading              - Show skeleton while loading
 * @param {Function} props.t                    - useTranslation t function
 */
export default function AuditEventTable({ events = [], includeCompanyColumn = false, loading = false, t }) {
    const colCount = includeCompanyColumn ? 6 : 5;

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <>
                {/* Desktop skeleton */}
                <div className="hidden sm:block">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('audit.column.datetime')}</TableHead>
                                <TableHead>{t('audit.column.event_type')}</TableHead>
                                {includeCompanyColumn && <TableHead>{t('audit.column.company')}</TableHead>}
                                <TableHead>{t('audit.column.user')}</TableHead>
                                <TableHead>{t('audit.column.ip')}</TableHead>
                                <TableHead>{t('audit.column.details')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <SkeletonRows count={6} cols={colCount} />
                        </TableBody>
                    </Table>
                </div>
                {/* Mobile skeleton */}
                <div className="sm:hidden space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-24 w-full rounded-md" />
                    ))}
                </div>
            </>
        );
    }

    // ── Empty ─────────────────────────────────────────────────────────────────
    if (events.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground gap-2">
                <span className="text-sm">{t('audit.empty')}</span>
            </div>
        );
    }

    // ── Desktop table ─────────────────────────────────────────────────────────
    return (
        <>
            <div className="hidden sm:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-40">{t('audit.column.datetime')}</TableHead>
                            <TableHead>{t('audit.column.event_type')}</TableHead>
                            {includeCompanyColumn && (
                                <TableHead>{t('audit.column.company')}</TableHead>
                            )}
                            <TableHead>{t('audit.column.user')}</TableHead>
                            <TableHead className="w-32">{t('audit.column.ip')}</TableHead>
                            <TableHead>{t('audit.column.details')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {events.map((event) => {
                            const isPreTenant = event.company_id === 'none';
                            return (
                                <TableRow key={event.id}>
                                    <TableCell className="font-mono text-xs whitespace-nowrap">
                                        {formatDateTime(event.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        <EventBadge eventType={event.event_type} t={t} />
                                    </TableCell>
                                    {includeCompanyColumn && (
                                        <TableCell className="text-xs">
                                            {isPreTenant ? (
                                                <span
                                                    className="text-muted-foreground"
                                                    title={t('audit.filter.pre_tenant')}
                                                >
                                                    —
                                                </span>
                                            ) : (
                                                event.company_id ?? '—'
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell className="text-xs font-mono max-w-[180px] truncate">
                                        {resolveUser(event)}
                                    </TableCell>
                                    <TableCell className="text-xs font-mono">
                                        {event.ip_address ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        <DetailsCell details={event.details} t={t} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            {/* Mobile: card-per-row layout */}
            <div className="sm:hidden space-y-2">
                {events.map((event) => (
                    <EventCard
                        key={event.id}
                        event={event}
                        includeCompanyColumn={includeCompanyColumn}
                        t={t}
                    />
                ))}
            </div>
        </>
    );
}
